import React from "react";
import PropTypes from "prop-types";
import LazyImage from "components/LazyImage";

import AvailabilityLabel from "../AvailabilityLabel";
import IconCheckmark from "../icons/Checkmark";
import JSONLDCar from "../JSONLDCar";
import FinancingData from "../FinancingData";
import FinancingDetailsOverlay from "../FinancingDetailsOverlay";
import withObjectFitSupportDetection from "../../hocs/withObjectFitSupportDetection";
import formatNumber from "../../utils/formatNumber";
import { formatMileage } from "../../utils/formatMileage";
import isPlaceholderImage from "../../utils/isPlaceholderImage";
import preventDefaultAction from "../../utils/preventDefaultAction";
import CarTags from "../CarTags";

const CardContent = (props) => {
  const {
    isLink,
    showVehicleInfos,
    objectFitSupported,
    brand,
    imageUrl,
    imageAlt = "",
    title,
    leasingOffer,
    firstRegistration,
    mileage,
    lessor,
    accessoriesConfigurable,
    maintenanceWearIncluded,
    insuranceIncluded,
    registrationIncluded,
    fuelType,
    fuelConsumption,
    transmission,
    emissionsCombined,
    power,
    colorNameManufacturer,
    availability,
    isLeasingTermSelectable,
    isMileagePerYearSelectable,
    powerConsumptionCombined,
    deposit,
    wltpCombined,
    wltpCarbonicEmission,
    hasWltpInfo,
    wltpPowerConsumptionCombined,
  } = props;

  const showPlaceholderImageNote = isPlaceholderImage(imageUrl);
  const { 0: bankName } = lessor.split(",");
  const isBMW = brand === "BMW";
  const isElectric = fuelType === "Elektro";
  const hasPowerConsumption = ["Elektro", "Hybrid", "Hybrid-Diesel"].includes(
    fuelType,
  );

  const formattedMileage = formatMileage(mileage);

  return (
    <div
      className={`flex flex-col items-center border border-grey rounded-b ${isLink
        ? "border-grey-light rounded-t hover:shadow-hover hover:border-secondary-light"
        : ""
        }`}
    >
      <div
        className={`bg-white w-full flex flex-col items-center relative rounded-t ${objectFitSupported ? "h-full" : ""
          }`}
      >
        <div className="detailed-offer-card__image">
          <LazyImage
            src={imageUrl}
            alt={imageAlt || title}
            layout="fixed"
            width={350}
            height={257}
          />
        </div>

        <div className="h-5">
          {showPlaceholderImageNote && (
            <span className="text-sm leading-none text-grey-darker py-px">
              Abbildung beispielhaft
            </span>
          )}
        </div>
      </div>

      <div className="pt-2 pb-4 w-full flex flex-col bg-white rounded-b">
        <div className="px-3">
          <p className="headline headline--6 break-words">{title}</p>
          {/* Tags, if any */}
          <div className="-ml-1 mt-2 flex flex-wrap">
            <CarTags product={props} />
          </div>
        </div>
        <div className="px-3 pt-2 mb-3">
          <div className="flex flex-col ">
            {showVehicleInfos && (
              <div className="mb-1 pb-3">
                <div className="flex">
                  <p className="w-1/2 paragraph--medium leading-normal">
                    {firstRegistration
                      ? `EZ: ${firstRegistration}`
                      : "Neuwagen"}
                  </p>
                  <p className="w-1/2 paragraph--medium leading-normal">
                    {formattedMileage}
                    {" "}
                    km
                  </p>
                </div>
                <>
                  <div className="flex">
                    <p className="w-1/2 paragraph--medium leading-normal">
                      {fuelType}
                    </p>
                    <p className="w-1/2 paragraph--medium leading-normal">
                      {power.ps}
                      {" "}
                      PS (
                      {power.kw}
                      {" "}
                      kW)
                    </p>
                  </div>
                  <div className="flex">
                    <p className="w-1/2 paragraph--medium leading-normal">
                      {transmission}
                    </p>
                    <p className="w-1/2 paragraph--medium leading-normal">
                      {colorNameManufacturer}
                    </p>
                  </div>
                  {!isElectric && (
                    <div className="flex">
                      <p className="w-1/2 paragraph--medium leading-normal">
                        {hasWltpInfo ? wltpCombined : fuelConsumption.combined}
                        {" "}
                        komb.
                      </p>
                      {emissionsCombined > 0 || wltpCarbonicEmission > 0 && (
                        <p className="w-1/2 paragraph--medium leading-normal">
                          {hasWltpInfo ? wltpCarbonicEmission : emissionsCombined}
                          {" "}
                          g/km CO2 komb.
                        </p>
                      )}
                    </div>
                  )}
                  {hasPowerConsumption && (powerConsumptionCombined || wltpPowerConsumptionCombined) && (
                    <div className="flex">
                      <p className="w-1/2 paragraph--medium leading-normal">
                        {wltpPowerConsumptionCombined ? wltpPowerConsumptionCombined : powerConsumptionCombined}
                        {" "}
                        kWh/100 km komb.
                      </p>
                    </div>
                  )}
                </>
              </div>
            )}
            {isLink ? (
              <div className="flex justify-between items-center py-2 mb-1 border-t border-b border-secondary">
                <div>
                  <p className="headline--4 font-bold text-secondary">
                    {leasingOffer.grossRate.priceString}
                    <span className="paragraph--small font-bold">
                      &nbsp;mtl.
                    </span>
                  </p>
                  <p className="paragraph--small">
                    {leasingOffer.netRate.priceString}
                    &nbsp;netto mtl.
                  </p>
                </div>
                <p className="btn btn--primary">zum Angebot</p>
              </div>
            ) : (
              <div className="py-2 mb-1 border-t border-b border-secondary">
                <p className="headline--4 font-bold text-secondary">
                  {leasingOffer.grossRate.priceString}
                  <span className="paragraph--small ">&nbsp;mtl.</span>
                  <span className="paragraph--small font-normal">
                    &nbsp;(
                    {leasingOffer.netRate.priceString}
                    &nbsp;netto mtl.)
                  </span>
                </p>
              </div>
            )}
            <div className="pt-2">
              {!showVehicleInfos && (
                <div className="flex">
                  <p className="w-1/2 paragraph--medium leading-normal">
                    {firstRegistration
                      ? `EZ: ${firstRegistration}`
                      : "Neuwagen"}
                  </p>
                  <p className="w-1/2 paragraph--medium leading-normal">
                    {formattedMileage}
                    {" "}
                    km
                  </p>
                </div>
              )}
              <div className="flex flex-wrap">
                <div className="w-1/2 paragraph--medium leading-normal">
                  {deposit}
                  &nbsp;€ Anzahlung
                </div>
                <p className="w-1/2 paragraph--medium leading-normal">
                  <AvailabilityLabel availability={availability} />
                </p>
                <p className=" w-1/2 paragraph--medium leading-normal">
                  {formatNumber(leasingOffer.leaseTerm)}
                  {" "}
                  Monate Laufzeit
                  {" "}
                  {isLeasingTermSelectable && (
                    <span className="text-grey-darker mb-1">(anpassbar)</span>
                  )}
                </p>
                <p className="w-1/2 paragraph--medium leading-normal">
                  {formatNumber(leasingOffer.mileagePerYear)}
                  {" "}
                  km frei / Jahr
                  {" "}
                  {isMileagePerYearSelectable && (
                    <span className="text-grey-darker mb-1">(anpassbar)</span>
                  )}
                </p>
              </div>
              {!isBMW && (
                <p className="pt-1 paragraph--medium leading-normal text-grey-darker">
                  {bankName}
                </p>
              )}
              <ul className="list-reset">
                {accessoriesConfigurable && (
                  <li className="paragraph--medium leading-normal flex items-baseline">
                    <IconCheckmark color="text-green" />
                    Ausstattung konfigurierbar
                  </li>
                )}
                {maintenanceWearIncluded && (
                  <li className="paragraph--medium leading-normal flex items-baseline">
                    <IconCheckmark color="text-green" />
                    Inklusive Wartung und Verschleiß
                  </li>
                )}
                {insuranceIncluded && (
                  <li className="paragraph--medium leading-normal flex items-baseline">
                    <IconCheckmark color="text-green" />
                    Inklusive Versicherung
                  </li>
                )}
                {registrationIncluded && (
                  <li className="paragraph--medium leading-normal flex items-baseline">
                    <IconCheckmark color="text-green" />
                    Inklusive Zulassung
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {!props.isBusinessOnly && (
          <div className="pt-3 mx-3 border-t border-grey-dark">
            {isBMW ? (
              <FinancingData displayInline {...props} />
            ) : (
              /* The <div> element has a child <button> element that allows keyboard interaction */
              /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
              <div
                className="paragraph--small text-left"
                onClick={(e) => preventDefaultAction(e)}
              >
                <FinancingDetailsOverlay
                  title={title}
                  financingData={<FinancingData {...props} />}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DetailedOfferCard = ({
  isLink = true,
  showVehicleInfos = false,
  // fromFinder = false,
  offerUrl,
  classNames,
  shortId,
  target = "_self",
  ...props
}) => (
  <div
    className={`detailed-offer-card ${classNames ? classNames.join(" ") : ""}`}
  >
    <>
      <JSONLDCar product={{ offerUrl, shortId, ...props }} />
      {isLink ? (
        <a id={shortId} href={offerUrl || ""} data-cy="cardOfferLink" target={target}>
          <CardContent
            isLink={isLink}
            showVehicleInfos={showVehicleInfos}
            {...props}
          />
        </a>
      ) : (
        <CardContent
          isLink={isLink}
          showVehicleInfos={showVehicleInfos}
          {...props}
        />
      )}
    </>
  </div>
);

DetailedOfferCard.propTypes = {
  classNames: PropTypes.array,
  offerUrl: PropTypes.string,
  imageUrl: PropTypes.string,
  imageAlt: PropTypes.string,
  title: PropTypes.string,
  isNewCar: PropTypes.bool,
  isBusinessOnly: PropTypes.bool,
  isPrivateOnly: PropTypes.bool,
  leasingOffer: PropTypes.object,
  firstRegistration: PropTypes.string,
  mileage: PropTypes.number,
  objectFitSupported: PropTypes.bool,
  lessor: PropTypes.string,
  accessoriesConfigurable: PropTypes.bool,
  maintenanceWearIncluded: PropTypes.bool,
  insuranceIncluded: PropTypes.bool,
  registrationIncluded: PropTypes.bool,
  fuelType: PropTypes.string,
  fuelConsumption: PropTypes.object,
  transmission: PropTypes.string,
  emissionsCombined: PropTypes.number,
  power: PropTypes.object,
  colorNameManufacturer: PropTypes.string,
  showVehicleInfos: PropTypes.bool,
  isLink: PropTypes.bool,
  target: PropTypes.oneOf(["_blank", "_self", "_parent", "_top", "framename"])
};

export default withObjectFitSupportDetection(DetailedOfferCard);
